import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { CREATE_SALES_DEAL, ASSIGN_TRANSFER_ATTORNEY, ASSIGN_BOND_ORIGINATOR, ASSIGN_BOND_ATTORNEY } from '../../graphql/sales-deals';
import { GET_ALL_AGENTS } from '../../graphql/agents';
import { GET_SERVICES_BY_TYPE } from '../../graphql/professional-services';
import QuickAddAgentModal from '../../components/QuickAddAgentModal';
import QuickAddProfessionalServiceModal from '../../components/QuickAddProfessionalServiceModal';

const AddSalesDeal = () => {
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        streetAddress: '',
        erfNumber: '',
        suburb: '',
        city: '',
        postalCode: '',
        sellingPrice: '',
        propertyType: 'HOUSE',
        notes: '',
        commissionInputMethod: 'PERCENTAGE',
        totalCommissionPercentage: '',
        totalCommissionAmount: '',
        paymentMethod: 'CASH',
        cashPayableDate: '',
        bondAmount: '',
        bondGrantDate: '',
        depositAmount: '',
        depositDueDate: '',
        otpDate: '',
        registrationDate: '',
        ficaChecked: false,
        transferAttorneyId: '',
        bondOriginatorId: '',
        bondAttorneyId: '',
        hasReferral: false,
        referralInfo: {
            referralNumber: '',
            company: '',
            agentName: '',
            agentEmail: '',
            agentPhone: '',
            commissionPercentage: ''
        },
        agents: [],
        sellers: [{
            name: '',
            isEntity: false,
            email: '',
            phone: '',
            idNumber: '',
            representativeName: '',
            representativeEmail: '',
            representativePhone: '',
            representativeIdNumber: ''
        }],
        purchasers: [{
            name: '',
            isEntity: false,
            email: '',
            phone: '',
            idNumber: '',
            representativeName: '',
            representativeEmail: '',
            representativePhone: '',
            representativeIdNumber: ''
        }]
    });

    const [errors, setErrors] = useState({});

    // Quick-add modal state
    const [addAgentForIndex, setAddAgentForIndex] = useState(null); // agent row index, or null
    const [addServiceFor, setAddServiceFor] = useState(null);       // 'transferAttorney' | 'bondOriginator' | 'bondAttorney' | null

    const { loading: loadingAgents, data: agentsData } = useQuery(GET_ALL_AGENTS);
    
    const { data: transferAttorneysData } = useQuery(GET_SERVICES_BY_TYPE, {
        variables: { type: 'ATTORNEY', activeOnly: true }
    });

    const { data: bondOriginatorsData } = useQuery(GET_SERVICES_BY_TYPE, {
        variables: { type: 'BOND_ORIGINATOR', activeOnly: true }
    });

    const [createSalesDeal, { loading: loadingCreate }] = useMutation(CREATE_SALES_DEAL);
    const [assignTransferAttorney] = useMutation(ASSIGN_TRANSFER_ATTORNEY);
    const [assignBondOriginator] = useMutation(ASSIGN_BOND_ORIGINATOR);
    const [assignBondAttorney] = useMutation(ASSIGN_BOND_ATTORNEY);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear error for this field when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleReferralInfoChange = (field, value) => {
        setFormData({
            ...formData,
            referralInfo: {
                ...formData.referralInfo,
                [field]: value
            }
        });
    };

    const handleAgentChange = (index, field, value) => {
        const updatedAgents = [...formData.agents];
        updatedAgents[index] = {
            ...updatedAgents[index],
            [field]: value
        };
        setFormData({
            ...formData,
            agents: updatedAgents
        });
    };

    const addAgent = () => {
        setFormData({
            ...formData,
            agents: [
                ...formData.agents,
                {
                    agentId: '',
                    commissionPercentage: '',
                    role: 'PRIMARY'
                }
            ]
        });
    };

    const removeAgent = (index) => {
        const updatedAgents = formData.agents.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            agents: updatedAgents
        });
    };

    const handleSellerChange = (index, field, value) => {
        const updatedSellers = [...formData.sellers];
        updatedSellers[index] = {
            ...updatedSellers[index],
            [field]: value
        };
        setFormData({
            ...formData,
            sellers: updatedSellers
        });
    };

    const addSeller = () => {
        setFormData({
            ...formData,
            sellers: [
                ...formData.sellers,
                {
                    name: '',
                    isEntity: false,
                    email: '',
                    phone: '',
                    idNumber: '',
                    representativeName: '',
                    representativeEmail: '',
                    representativePhone: '',
                    representativeIdNumber: ''
                }
            ]
        });
    };

    const removeSeller = (index) => {
        const updatedSellers = formData.sellers.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            sellers: updatedSellers
        });
    };

    const handlePurchaserChange = (index, field, value) => {
        const updatedPurchasers = [...formData.purchasers];
        updatedPurchasers[index] = {
            ...updatedPurchasers[index],
            [field]: value
        };
        setFormData({
            ...formData,
            purchasers: updatedPurchasers
        });
    };

    const addPurchaser = () => {
        setFormData({
            ...formData,
            purchasers: [
                ...formData.purchasers,
                {
                    name: '',
                    isEntity: false,
                    email: '',
                    phone: '',
                    idNumber: '',
                    representativeName: '',
                    representativeEmail: '',
                    representativePhone: '',
                    representativeIdNumber: ''
                }
            ]
        });
    };

    const removePurchaser = (index) => {
        const updatedPurchasers = formData.purchasers.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            purchasers: updatedPurchasers
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.streetAddress.trim()) {
            newErrors.streetAddress = 'Street address is required';
        }

        if (!formData.suburb.trim()) {
            newErrors.suburb = 'Suburb is required';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0) {
            newErrors.sellingPrice = 'Valid selling price is required';
        }

        if (formData.agents.length === 0) {
            newErrors.agents = 'At least one agent is required';
        }

        // Validate agents
        formData.agents.forEach((agent, index) => {
            if (!agent.agentId) {
                newErrors[`agent_${index}_id`] = 'Agent selection is required';
            }
            if (!agent.commissionPercentage || parseFloat(agent.commissionPercentage) <= 0) {
                newErrors[`agent_${index}_commission`] = 'Valid commission share is required';
            }
        });

        // Agents' shares are slices of the post-referral commission; the
        // company keeps whatever is left, so the only constraint is the
        // agents can't take more than 100% combined.
        if (formData.agents.length > 0) {
            const sum = formData.agents.reduce(
                (acc, a) => acc + (parseFloat(a.commissionPercentage) || 0),
                0
            );
            if (sum > 100.01) {
                newErrors.agents = `Agent commission shares cannot exceed 100% (currently ${sum.toFixed(2)}%)`;
            }
        }

        // Validate commission input
        if (formData.commissionInputMethod === 'PERCENTAGE') {
            if (!formData.totalCommissionPercentage || parseFloat(formData.totalCommissionPercentage) <= 0) {
                newErrors.totalCommissionPercentage = 'Commission percentage is required';
            }
        } else if (formData.commissionInputMethod === 'AMOUNT') {
            if (!formData.totalCommissionAmount || parseFloat(formData.totalCommissionAmount) <= 0) {
                newErrors.totalCommissionAmount = 'Commission amount is required';
            }
        }

        // Validate payment method specific fields
        if (formData.paymentMethod === 'CASH') {
            if (!formData.cashPayableDate) {
                newErrors.cashPayableDate = 'Cash payable date is required when payment method is Cash';
            }
        } else if (formData.paymentMethod === 'BOND') {
            if (!formData.bondAmount || parseFloat(formData.bondAmount) <= 0) {
                newErrors.bondAmount = 'Bond amount is required when payment method is Bond';
            }
            if (!formData.bondGrantDate) {
                newErrors.bondGrantDate = 'Bond grant date is required when payment method is Bond';
            }
        }

        // Validate sellers - at least one seller with a name is required
        const validSellers = formData.sellers.filter(seller => seller.name.trim());
        if (validSellers.length === 0) {
            newErrors.sellers = 'At least one seller with a name is required';
        }

        // Validate purchasers - at least one purchaser with a name is required
        const validPurchasers = formData.purchasers.filter(purchaser => purchaser.name.trim());
        if (validPurchasers.length === 0) {
            newErrors.purchasers = 'At least one purchaser with a name is required';
        }

        // Validate referral info if hasReferral is checked
        if (formData.hasReferral) {
            if (!formData.referralInfo.company.trim()) {
                newErrors.referralCompany = 'Company name is required for external referrals';
            }
            if (!formData.referralInfo.agentName.trim()) {
                newErrors.referralAgentName = 'External agent name is required for referrals';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateTotalCommission = () => {
        return formData.agents.reduce((total, agent) => {
            const commission = parseFloat(agent.commissionPercentage) || 0;
            return total + commission;
        }, 0);
    };

    const calculateCommissionAmount = () => {
        const sellingPrice = parseFloat(formData.sellingPrice) || 0;
        const totalCommission = calculateTotalCommission();
        return (sellingPrice * totalCommission / 100);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            try {
                const mutationInput = {
                    streetAddress: formData.streetAddress,
                    erfNumber: formData.erfNumber || null,
                    suburb: formData.suburb,
                    city: formData.city,
                    postalCode: formData.postalCode || null,
                    sellingPrice: parseFloat(formData.sellingPrice),
                    propertyType: formData.propertyType,
                    commissionInputMethod: formData.commissionInputMethod,
                    totalCommissionPercentage: formData.commissionInputMethod === 'PERCENTAGE' ? parseFloat(formData.totalCommissionPercentage) : null,
                    totalCommissionAmount: formData.commissionInputMethod === 'AMOUNT' ? parseFloat(formData.totalCommissionAmount) : null,
                    paymentMethod: formData.paymentMethod,
                    cashPayableDate: formData.paymentMethod === 'CASH' ? formData.cashPayableDate : null,
                    bondAmount: formData.paymentMethod === 'BOND' && formData.bondAmount ? parseFloat(formData.bondAmount) : null,
                    bondGrantDate: formData.paymentMethod === 'BOND' ? formData.bondGrantDate : null,
                    depositAmount: formData.depositAmount ? parseFloat(formData.depositAmount) : null,
                    depositDueDate: formData.depositDueDate || null,
                    otpDate: formData.otpDate || null,
                    registrationDate: formData.registrationDate || null,
                    ficaChecked: formData.ficaChecked,
                    notes: formData.notes || null,
                    agents: formData.agents.map(agent => ({
                        agentId: agent.agentId,
                        commissionPercentage: parseFloat(agent.commissionPercentage),
                        role: agent.role
                    })),
                    sellers: formData.sellers.filter(s => s.name.trim()),
                    purchasers: formData.purchasers.filter(p => p.name.trim()),
                    hasReferral: formData.hasReferral,
                    referralInfo: formData.hasReferral ? {
                        referralNumber: formData.referralInfo.referralNumber || null,
                        company: formData.referralInfo.company,
                        agentName: formData.referralInfo.agentName,
                        agentEmail: formData.referralInfo.agentEmail || null,
                        agentPhone: formData.referralInfo.agentPhone || null,
                        commissionPercentage: formData.referralInfo.commissionPercentage ? parseFloat(formData.referralInfo.commissionPercentage) : null
                    } : null
                };

                const { data } = await createSalesDeal({
                    variables: {
                        inputData: mutationInput
                    }
                });

                const dealId = data.createSalesDeal.id;

                // Assign professional services if selected
                if (formData.transferAttorneyId) {
                    await assignTransferAttorney({
                        variables: {
                            dealId: dealId,
                            inputData: {
                                professionalServiceId: formData.transferAttorneyId
                            }
                        }
                    });
                }

                if (formData.bondOriginatorId) {
                    await assignBondOriginator({
                        variables: {
                            dealId: dealId,
                            inputData: {
                                professionalServiceId: formData.bondOriginatorId
                            }
                        }
                    });
                }

                if (formData.bondAttorneyId) {
                    await assignBondAttorney({
                        variables: {
                            dealId: dealId,
                            inputData: {
                                professionalServiceId: formData.bondAttorneyId
                            }
                        }
                    });
                }

                navigate(`/sales/deals/${dealId}`);
            } catch (error) {
                console.error('Error creating sales deal:', error);
                setErrors({
                    ...errors,
                    submit: error.message
                });
            }
        }
    };

    const agentOptions = agentsData?.agents?.items || [];
    const totalCommission = calculateTotalCommission();
    const commissionAmount = calculateCommissionAmount();

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-semibold flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Sales Deal
                </h1>
                <Link to="/sales/deals" className="flex items-center text-blue-600 hover:text-blue-800">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Sales Deals
                </Link>
            </div>

            {errors.submit && (
                <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Error!</strong>
                    <span className="block sm:inline"> {errors.submit}</span>
                </div>
            )}

            <div className="bg-white shadow rounded-lg p-6">
                <form onSubmit={handleSubmit}>
                    {/* Property Information */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Property Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Street Address */}
                            <div className="md:col-span-2">
                                <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
                                    Street Address*
                                </label>
                                <input
                                    type="text"
                                    id="streetAddress"
                                    name="streetAddress"
                                    value={formData.streetAddress}
                                    onChange={handleChange}
                                    className={`block w-full px-3 py-2 border ${errors.streetAddress ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    placeholder="e.g. 123 Main Street"
                                />
                                {errors.streetAddress && (
                                    <p className="mt-1 text-sm text-red-600">{errors.streetAddress}</p>
                                )}
                            </div>

                            {/* ERF Number */}
                            <div>
                                <label htmlFor="erfNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    ERF Number
                                </label>
                                <input
                                    type="text"
                                    id="erfNumber"
                                    name="erfNumber"
                                    value={formData.erfNumber}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="e.g. ERF 123"
                                />
                            </div>

                            {/* Suburb */}
                            <div>
                                <label htmlFor="suburb" className="block text-sm font-medium text-gray-700 mb-1">
                                    Suburb*
                                </label>
                                <input
                                    type="text"
                                    id="suburb"
                                    name="suburb"
                                    value={formData.suburb}
                                    onChange={handleChange}
                                    className={`block w-full px-3 py-2 border ${errors.suburb ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                {errors.suburb && (
                                    <p className="mt-1 text-sm text-red-600">{errors.suburb}</p>
                                )}
                            </div>

                            {/* City */}
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                    City*
                                </label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className={`block w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                {errors.city && (
                                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                                )}
                            </div>

                            {/* Postal Code */}
                            <div>
                                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                                    Postal Code
                                </label>
                                <input
                                    type="text"
                                    id="postalCode"
                                    name="postalCode"
                                    value={formData.postalCode}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>

                            {/* Property Type */}
                            <div>
                                <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-1">
                                    Property Type
                                </label>
                                <select
                                    id="propertyType"
                                    name="propertyType"
                                    value={formData.propertyType}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="HOUSE">House</option>
                                    <option value="APARTMENT">Apartment</option>
                                    <option value="TOWNHOUSE">Townhouse</option>
                                    <option value="FLAT">Flat</option>
                                    <option value="DUPLEX">Duplex</option>
                                    <option value="VACANT_LAND">Vacant Land</option>
                                    <option value="COMMERCIAL">Commercial</option>
                                    <option value="FARM">Farm</option>
                                </select>
                            </div>

                        </div>
                    </div>

                    {/* Financial Information */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Selling Price */}
                            <div>
                                <label htmlFor="sellingPrice" className="block text-sm font-medium text-gray-700 mb-1">
                                    Selling Price*
                                </label>
                                <input
                                    type="number"
                                    id="sellingPrice"
                                    name="sellingPrice"
                                    value={formData.sellingPrice}
                                    onChange={handleChange}
                                    className={`block w-full px-3 py-2 border ${errors.sellingPrice ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    placeholder="e.g. 1500000"
                                    min="0"
                                    step="1000"
                                />
                                {errors.sellingPrice && (
                                    <p className="mt-1 text-sm text-red-600">{errors.sellingPrice}</p>
                                )}
                            </div>

                            {/* Commission Input Method */}
                            <div>
                                <label htmlFor="commissionInputMethod" className="block text-sm font-medium text-gray-700 mb-1">
                                    Commission Input Method*
                                </label>
                                <select
                                    id="commissionInputMethod"
                                    name="commissionInputMethod"
                                    value={formData.commissionInputMethod}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="PERCENTAGE">Percentage</option>
                                    <option value="AMOUNT">Amount (Excl. VAT)</option>
                                </select>
                            </div>

                            {/* Commission Percentage Input (if percentage selected) */}
                            {formData.commissionInputMethod === 'PERCENTAGE' && (
                                <div>
                                    <label htmlFor="totalCommissionPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                                        Commission Percentage* (Excl. VAT)
                                    </label>
                                    <input
                                        type="number"
                                        id="totalCommissionPercentage"
                                        name="totalCommissionPercentage"
                                        value={formData.totalCommissionPercentage}
                                        onChange={handleChange}
                                        className={`block w-full px-3 py-2 border ${errors.totalCommissionPercentage ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        placeholder="e.g. 5.5"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                    />
                                    {errors.totalCommissionPercentage && (
                                        <p className="mt-1 text-sm text-red-600">{errors.totalCommissionPercentage}</p>
                                    )}
                                    {formData.totalCommissionPercentage && formData.sellingPrice && (
                                        <p className="mt-1 text-sm text-gray-500">
                                            Amount: {new Intl.NumberFormat('en-ZA', {
                                                style: 'currency',
                                                currency: 'ZAR',
                                                minimumFractionDigits: 0
                                            }).format((parseFloat(formData.sellingPrice) * parseFloat(formData.totalCommissionPercentage)) / 100)}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Commission Amount Input (if amount selected) */}
                            {formData.commissionInputMethod === 'AMOUNT' && (
                                <div>
                                    <label htmlFor="totalCommissionAmount" className="block text-sm font-medium text-gray-700 mb-1">
                                        Commission Amount* (Excl. VAT)
                                    </label>
                                    <input
                                        type="number"
                                        id="totalCommissionAmount"
                                        name="totalCommissionAmount"
                                        value={formData.totalCommissionAmount}
                                        onChange={handleChange}
                                        className={`block w-full px-3 py-2 border ${errors.totalCommissionAmount ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        placeholder="e.g. 82500"
                                        min="0"
                                        step="100"
                                    />
                                    {errors.totalCommissionAmount && (
                                        <p className="mt-1 text-sm text-red-600">{errors.totalCommissionAmount}</p>
                                    )}
                                    {formData.totalCommissionAmount && formData.sellingPrice && (
                                        <p className="mt-1 text-sm text-gray-500">
                                            Percentage: {((parseFloat(formData.totalCommissionAmount) / parseFloat(formData.sellingPrice)) * 100).toFixed(2)}%
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Payment Method */}
                            <div>
                                <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                                    Payment Method*
                                </label>
                                <select
                                    id="paymentMethod"
                                    name="paymentMethod"
                                    value={formData.paymentMethod}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="CASH">Cash</option>
                                    <option value="BOND">Bond</option>
                                </select>
                            </div>

                            {/* Cash Payable Date (if Cash selected) */}
                            {formData.paymentMethod === 'CASH' && (
                                <div>
                                    <label htmlFor="cashPayableDate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Cash Payable Date*
                                    </label>
                                    <input
                                        type="date"
                                        id="cashPayableDate"
                                        name="cashPayableDate"
                                        value={formData.cashPayableDate}
                                        onChange={handleChange}
                                        className={`block w-full px-3 py-2 border ${errors.cashPayableDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                    {errors.cashPayableDate && (
                                        <p className="mt-1 text-sm text-red-600">{errors.cashPayableDate}</p>
                                    )}
                                </div>
                            )}

                            {/* Bond Amount and Grant Date (if Bond selected) */}
                            {formData.paymentMethod === 'BOND' && (
                                <>
                                    <div>
                                        <label htmlFor="bondAmount" className="block text-sm font-medium text-gray-700 mb-1">
                                            Bond Amount*
                                        </label>
                                        <input
                                            type="number"
                                            id="bondAmount"
                                            name="bondAmount"
                                            value={formData.bondAmount}
                                            onChange={handleChange}
                                            className={`block w-full px-3 py-2 border ${errors.bondAmount ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                            min="0"
                                            step="1000"
                                            placeholder="e.g. 1200000"
                                        />
                                        {errors.bondAmount && (
                                            <p className="mt-1 text-sm text-red-600">{errors.bondAmount}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="bondGrantDate" className="block text-sm font-medium text-gray-700 mb-1">
                                            Bond Grant Date* (Suspensive Condition)
                                        </label>
                                        <input
                                            type="date"
                                            id="bondGrantDate"
                                            name="bondGrantDate"
                                            value={formData.bondGrantDate}
                                            onChange={handleChange}
                                            className={`block w-full px-3 py-2 border ${errors.bondGrantDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        />
                                        {errors.bondGrantDate && (
                                            <p className="mt-1 text-sm text-red-600">{errors.bondGrantDate}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Deposit Amount */}
                            <div>
                                <label htmlFor="depositAmount" className="block text-sm font-medium text-gray-700 mb-1">
                                    Deposit Amount
                                </label>
                                <input
                                    type="number"
                                    id="depositAmount"
                                    name="depositAmount"
                                    value={formData.depositAmount}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    min="0"
                                    step="1000"
                                />
                            </div>

                            {/* Deposit Due Date */}
                            <div>
                                <label htmlFor="depositDueDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    Deposit Due Date
                                </label>
                                <input
                                    type="date"
                                    id="depositDueDate"
                                    name="depositDueDate"
                                    value={formData.depositDueDate}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Timeline Section */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Timeline Tracking</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* OTP Date */}
                            <div>
                                <label htmlFor="otpDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    OTP Date (Offer to Purchase)
                                </label>
                                <input
                                    type="date"
                                    id="otpDate"
                                    name="otpDate"
                                    value={formData.otpDate}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    When the offer to purchase was signed
                                </p>
                            </div>

                            {/* Registration Date */}
                            <div>
                                <label htmlFor="registrationDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    Registration Date
                                </label>
                                <input
                                    type="date"
                                    id="registrationDate"
                                    name="registrationDate"
                                    value={formData.registrationDate}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    When property was registered at deeds office
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Agents Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-medium text-gray-900">Agents & Commission</h2>
                            <button
                                type="button"
                                onClick={addAgent}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add Agent
                            </button>
                        </div>

                        {errors.agents && (
                            <p className="mb-4 text-sm text-red-600">{errors.agents}</p>
                        )}

                        {formData.agents.map((agent, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-md font-medium text-gray-900">Agent {index + 1}</h3>
                                    <button
                                        type="button"
                                        onClick={() => removeAgent(index)}
                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Agent Selection */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Agent*
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setAddAgentForIndex(index)}
                                                className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                            >
                                                + New
                                            </button>
                                        </div>
                                        <select
                                            value={agent.agentId}
                                            onChange={(e) => handleAgentChange(index, 'agentId', e.target.value)}
                                            className={`block w-full px-3 py-2 border ${errors[`agent_${index}_id`] ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                            disabled={loadingAgents}
                                        >
                                            <option value="">Select Agent</option>
                                            {agentOptions.map(agentOption => (
                                                <option key={agentOption.id} value={agentOption.id}>
                                                    {agentOption.firstName} {agentOption.lastName}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`agent_${index}_id`] && (
                                            <p className="mt-1 text-sm text-red-600">{errors[`agent_${index}_id`]}</p>
                                        )}
                                    </div>

                                    {/* Share of total commission */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Share of Commission %*
                                        </label>
                                        <input
                                            type="number"
                                            value={agent.commissionPercentage}
                                            onChange={(e) => handleAgentChange(index, 'commissionPercentage', e.target.value)}
                                            className={`block w-full px-3 py-2 border ${errors[`agent_${index}_commission`] ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                            placeholder="e.g. 50"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">% of the post-referral commission. Company keeps the unallocated remainder.</p>
                                        {errors[`agent_${index}_commission`] && (
                                            <p className="mt-1 text-sm text-red-600">{errors[`agent_${index}_commission`]}</p>
                                        )}
                                    </div>

                                    {/* Role */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Role
                                        </label>
                                        <select
                                            value={agent.role}
                                            onChange={(e) => handleAgentChange(index, 'role', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        >
                                            <option value="PRIMARY">Primary Agent</option>
                                            <option value="SECONDARY">Secondary Agent</option>
                                            <option value="TERTIARY">Tertiary Agent</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {formData.agents.length === 0 && (
                            <div className="text-center py-4 text-gray-500">
                                No agents added yet. Click "Add Agent" to get started.
                            </div>
                        )}
                    </div>

                    {/* External Referral Section */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">External Referral</h2>

                        {/* Has Referral Checkbox */}
                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.hasReferral}
                                    onChange={(e) => setFormData({ ...formData, hasReferral: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">
                                    This deal came from an external referral
                                </span>
                            </label>
                        </div>

                        {/* Referral Details - Conditional */}
                        {formData.hasReferral && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Company Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Company Name*
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.referralInfo.company}
                                            onChange={(e) => handleReferralInfoChange('company', e.target.value)}
                                            className={`block w-full px-3 py-2 border ${errors.referralCompany ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                            placeholder="Referring company name"
                                        />
                                        {errors.referralCompany && (
                                            <p className="mt-1 text-sm text-red-600">{errors.referralCompany}</p>
                                        )}
                                    </div>

                                    {/* External Agent Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            External Agent Name*
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.referralInfo.agentName}
                                            onChange={(e) => handleReferralInfoChange('agentName', e.target.value)}
                                            className={`block w-full px-3 py-2 border ${errors.referralAgentName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                            placeholder="Agent's full name"
                                        />
                                        {errors.referralAgentName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.referralAgentName}</p>
                                        )}
                                    </div>

                                    {/* Agent Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Agent Email
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.referralInfo.agentEmail}
                                            onChange={(e) => handleReferralInfoChange('agentEmail', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="agent@company.com"
                                        />
                                    </div>

                                    {/* Agent Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Agent Phone
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.referralInfo.agentPhone}
                                            onChange={(e) => handleReferralInfoChange('agentPhone', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="+27 12 345 6789"
                                        />
                                    </div>

                                    {/* Commission Percentage */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Commission %
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.referralInfo.commissionPercentage}
                                            onChange={(e) => handleReferralInfoChange('commissionPercentage', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="e.g. 1.5"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                        />
                                    </div>

                                    {/* Referral Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Referral Number
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.referralInfo.referralNumber}
                                            onChange={(e) => handleReferralInfoChange('referralNumber', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="Reference number (optional)"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sellers Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-medium text-gray-900">Sellers</h2>
                            <button
                                type="button"
                                onClick={addSeller}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add Seller
                            </button>
                        </div>

                        {errors.sellers && (
                            <p className="mb-4 text-sm text-red-600">{errors.sellers}</p>
                        )}

                        {formData.sellers.map((seller, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-md font-medium text-gray-900">Seller {index + 1}</h3>
                                    {formData.sellers.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeSeller(index)}
                                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                {/* Is Entity Checkbox */}
                                <div className="mb-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={seller.isEntity || false}
                                            onChange={(e) => handleSellerChange(index, 'isEntity', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">
                                            This seller is an entity (company/trust/etc)
                                        </span>
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Seller/Entity Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {seller.isEntity ? 'Entity Name*' : 'Name*'}
                                        </label>
                                        <input
                                            type="text"
                                            value={seller.name}
                                            onChange={(e) => handleSellerChange(index, 'name', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder={seller.isEntity ? "Company/Trust name" : "Full name"}
                                        />
                                    </div>

                                    {/* Seller Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {seller.isEntity ? 'Entity Email' : 'Email'}
                                        </label>
                                        <input
                                            type="email"
                                            value={seller.email}
                                            onChange={(e) => handleSellerChange(index, 'email', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="email@example.com"
                                        />
                                    </div>

                                    {/* Seller Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {seller.isEntity ? 'Entity Phone' : 'Phone'}
                                        </label>
                                        <input
                                            type="tel"
                                            value={seller.phone}
                                            onChange={(e) => handleSellerChange(index, 'phone', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="+27 12 345 6789"
                                        />
                                    </div>

                                    {/* Seller ID/Reg Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {seller.isEntity ? 'Registration Number' : 'ID Number'}
                                        </label>
                                        <input
                                            type="text"
                                            value={seller.idNumber}
                                            onChange={(e) => handleSellerChange(index, 'idNumber', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder={seller.isEntity ? "Company registration number" : "ID or passport number"}
                                        />
                                    </div>
                                </div>

                                {/* Representative Fields - Only show if isEntity is true */}
                                {seller.isEntity && (
                                    <div className="mt-4 pt-4 border-t border-gray-300">
                                        <h4 className="text-sm font-medium text-gray-900 mb-3">Representative Details</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Representative Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Name*
                                                </label>
                                                <input
                                                    type="text"
                                                    value={seller.representativeName}
                                                    onChange={(e) => handleSellerChange(index, 'representativeName', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="Full name of representative"
                                                />
                                            </div>

                                            {/* Representative Email */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={seller.representativeEmail}
                                                    onChange={(e) => handleSellerChange(index, 'representativeEmail', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="email@example.com"
                                                />
                                            </div>

                                            {/* Representative Phone */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={seller.representativePhone}
                                                    onChange={(e) => handleSellerChange(index, 'representativePhone', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="+27 12 345 6789"
                                                />
                                            </div>

                                            {/* Representative ID Number */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative ID Number
                                                </label>
                                                <input
                                                    type="text"
                                                    value={seller.representativeIdNumber}
                                                    onChange={(e) => handleSellerChange(index, 'representativeIdNumber', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="ID or passport number"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Purchasers Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-medium text-gray-900">Purchasers</h2>
                            <button
                                type="button"
                                onClick={addPurchaser}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add Purchaser
                            </button>
                        </div>

                        {errors.purchasers && (
                            <p className="mb-4 text-sm text-red-600">{errors.purchasers}</p>
                        )}

                        {formData.purchasers.map((purchaser, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-md font-medium text-gray-900">Purchaser {index + 1}</h3>
                                    {formData.purchasers.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removePurchaser(index)}
                                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                {/* Is Entity Checkbox */}
                                <div className="mb-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={purchaser.isEntity || false}
                                            onChange={(e) => handlePurchaserChange(index, 'isEntity', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">
                                            This purchaser is an entity (company/trust/etc)
                                        </span>
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Purchaser/Entity Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {purchaser.isEntity ? 'Entity Name*' : 'Name*'}
                                        </label>
                                        <input
                                            type="text"
                                            value={purchaser.name}
                                            onChange={(e) => handlePurchaserChange(index, 'name', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder={purchaser.isEntity ? "Company/Trust name" : "Full name"}
                                        />
                                    </div>

                                    {/* Purchaser Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {purchaser.isEntity ? 'Entity Email' : 'Email'}
                                        </label>
                                        <input
                                            type="email"
                                            value={purchaser.email}
                                            onChange={(e) => handlePurchaserChange(index, 'email', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="email@example.com"
                                        />
                                    </div>

                                    {/* Purchaser Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {purchaser.isEntity ? 'Entity Phone' : 'Phone'}
                                        </label>
                                        <input
                                            type="tel"
                                            value={purchaser.phone}
                                            onChange={(e) => handlePurchaserChange(index, 'phone', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="+27 12 345 6789"
                                        />
                                    </div>

                                    {/* Purchaser ID/Reg Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {purchaser.isEntity ? 'Registration Number' : 'ID Number'}
                                        </label>
                                        <input
                                            type="text"
                                            value={purchaser.idNumber}
                                            onChange={(e) => handlePurchaserChange(index, 'idNumber', e.target.value)}
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder={purchaser.isEntity ? "Company registration number" : "ID or passport number"}
                                        />
                                    </div>
                                </div>

                                {/* Representative Fields - Only show if isEntity is true */}
                                {purchaser.isEntity && (
                                    <div className="mt-4 pt-4 border-t border-gray-300">
                                        <h4 className="text-sm font-medium text-gray-900 mb-3">Representative Details</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Representative Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Name*
                                                </label>
                                                <input
                                                    type="text"
                                                    value={purchaser.representativeName}
                                                    onChange={(e) => handlePurchaserChange(index, 'representativeName', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="Full name of representative"
                                                />
                                            </div>

                                            {/* Representative Email */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={purchaser.representativeEmail}
                                                    onChange={(e) => handlePurchaserChange(index, 'representativeEmail', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="email@example.com"
                                                />
                                            </div>

                                            {/* Representative Phone */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={purchaser.representativePhone}
                                                    onChange={(e) => handlePurchaserChange(index, 'representativePhone', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="+27 12 345 6789"
                                                />
                                            </div>

                                            {/* Representative ID Number */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Representative ID Number
                                                </label>
                                                <input
                                                    type="text"
                                                    value={purchaser.representativeIdNumber}
                                                    onChange={(e) => handlePurchaserChange(index, 'representativeIdNumber', e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="ID or passport number"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Professional Services Section */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Professional Services</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Transfer Attorney */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor="transferAttorneyId" className="block text-sm font-medium text-gray-700">
                                        Transfer Attorney
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setAddServiceFor('transferAttorney')}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                    >
                                        + New
                                    </button>
                                </div>
                                <select
                                    id="transferAttorneyId"
                                    name="transferAttorneyId"
                                    value={formData.transferAttorneyId}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">Select Transfer Attorney</option>
                                    {transferAttorneysData?.professionalServicesByType?.map(attorney => (
                                        <option key={attorney.id} value={attorney.id}>
                                            {attorney.companyName || attorney.tradingName}
                                            {attorney.performance?.clientSatisfaction &&
                                                ` (★ ${attorney.performance.clientSatisfaction.toFixed(1)})`
                                            }
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bond Originator */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor="bondOriginatorId" className="block text-sm font-medium text-gray-700">
                                        Bond Originator
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setAddServiceFor('bondOriginator')}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                    >
                                        + New
                                    </button>
                                </div>
                                <select
                                    id="bondOriginatorId"
                                    name="bondOriginatorId"
                                    value={formData.bondOriginatorId}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">Select Bond Originator</option>
                                    {bondOriginatorsData?.professionalServicesByType?.map(originator => (
                                        <option key={originator.id} value={originator.id}>
                                            {originator.companyName || originator.tradingName}
                                            {originator.performance?.clientSatisfaction &&
                                                ` (★ ${originator.performance.clientSatisfaction.toFixed(1)})`
                                            }
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bond Attorney */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor="bondAttorneyId" className="block text-sm font-medium text-gray-700">
                                        Bond Attorney
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setAddServiceFor('bondAttorney')}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                    >
                                        + New
                                    </button>
                                </div>
                                <select
                                    id="bondAttorneyId"
                                    name="bondAttorneyId"
                                    value={formData.bondAttorneyId}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">Select Bond Attorney</option>
                                    {transferAttorneysData?.professionalServicesByType?.map(attorney => (
                                        <option key={attorney.id} value={attorney.id}>
                                            {attorney.companyName || attorney.tradingName}
                                            {attorney.performance?.clientSatisfaction &&
                                                ` (★ ${attorney.performance.clientSatisfaction.toFixed(1)})`
                                            }
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                    Handles bond registration (if bond required)
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h2>
                        
                        {/* FICA Checked */}
                        <div className="flex items-center space-x-3 mb-4">
                            <input
                                type="checkbox"
                                id="ficaChecked"
                                name="ficaChecked"
                                checked={formData.ficaChecked}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="ficaChecked" className="block text-sm font-medium text-gray-700">
                                FICA Checked
                            </label>
                        </div>

                        {/* Notes */}
                        <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                                Notes
                            </label>
                            <textarea
                                id="notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows="3"
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Additional notes about this deal..."
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="mt-6 flex items-center justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/sales/deals')}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loadingCreate}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {loadingCreate ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating Deal...
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Create Deal
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            <QuickAddAgentModal
                open={addAgentForIndex !== null}
                onClose={() => setAddAgentForIndex(null)}
                onCreated={(agent) => {
                    if (agent?.id && addAgentForIndex !== null) {
                        handleAgentChange(addAgentForIndex, 'agentId', agent.id);
                    }
                }}
            />

            <QuickAddProfessionalServiceModal
                open={addServiceFor !== null}
                onClose={() => setAddServiceFor(null)}
                type={addServiceFor === 'bondOriginator' ? 'BOND_ORIGINATOR' : 'ATTORNEY'}
                title={
                    addServiceFor === 'transferAttorney' ? 'Add Transfer Attorney' :
                    addServiceFor === 'bondOriginator' ? 'Add Bond Originator' :
                    addServiceFor === 'bondAttorney' ? 'Add Bond Attorney' : undefined
                }
                onCreated={(service) => {
                    if (!service?.id) return;
                    if (addServiceFor === 'transferAttorney') {
                        setFormData((prev) => ({ ...prev, transferAttorneyId: service.id }));
                    } else if (addServiceFor === 'bondOriginator') {
                        setFormData((prev) => ({ ...prev, bondOriginatorId: service.id }));
                    } else if (addServiceFor === 'bondAttorney') {
                        setFormData((prev) => ({ ...prev, bondAttorneyId: service.id }));
                    }
                }}
            />
        </div>
    );
};

export default AddSalesDeal;